import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ChevronLeft, 
  ChevronRight, 
  Loader2,
  MapPin,
  User,
  Plus,
  Pencil,
  Trash2,
  CalendarPlus,
  Users,
  CheckCircle2,
  ArrowRight,
  Flag,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, addDays, addWeeks, isToday } from 'date-fns';
import { Textarea } from '../../components/ui/textarea';
import { computeScheduleFlags } from '../../utils/scheduleFlags';
import { BackToDiary } from '../../components/BackToDiary';

// Plan-vs-actual flag styling (dark portal theme).
const FLAG_META = {
  logged_not_scheduled: { label: 'Not scheduled', color: '#F5A623', bg: 'rgba(245,166,35,0.15)', border: 'rgba(245,166,35,0.3)' },
  scheduled_not_logged: { label: 'Not logged', color: '#60A5FA', bg: 'rgba(96,165,250,0.15)', border: 'rgba(96,165,250,0.3)' },
  logged_elsewhere: { label: 'Logged elsewhere', color: '#C084FC', bg: 'rgba(192,132,252,0.15)', border: 'rgba(192,132,252,0.3)' },
};

/**
 * ReconcilePanel — "Plan vs Actual" for the current week. Lists every
 * (person, day) where the schedule (plan) and diary labour log (actual)
 * disagree. Read-only flags — nothing changes either side. Admin sees all
 * jobs; surveyors are scoped to their assigned jobs (enforced server-side).
 * Defined at module scope so it never remounts on parent re-render.
 */
export function ReconcilePanel({ api, weekStart, onOpenSchedule, onLogInDiary }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const start = format(weekStart, 'yyyy-MM-dd');
        const end = format(addDays(weekStart, 6), 'yyyy-MM-dd');
        const res = await api.get(`/portal/schedule-reconciliation?start=${start}&end=${end}`);
        if (!cancelled) setData(res.data);
      } catch (_e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, weekStart]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  const rows = data?.rows || [];
  if (rows.length === 0) {
    return (
      <Card className="bg-[#2A3240] border-white/10" data-testid="reconcile-empty">
        <CardContent className="p-8 flex flex-col items-center text-center gap-3">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
          <p className="text-white font-semibold">Everything lines up this week</p>
          <p className="text-gray-400 text-sm">The schedule and the site diaries match for every person, every day.</p>
        </CardContent>
      </Card>
    );
  }

  const byDate = {};
  rows.forEach(r => { (byDate[r.date] = byDate[r.date] || []).push(r); });
  const dates = Object.keys(byDate).sort();
  const refText = (arr) => (arr && arr.length ? arr.map(x => x.project_ref || '—').join(', ') : '—');

  return (
    <div className="space-y-4" data-testid="reconcile-panel">
      <div className="flex flex-wrap gap-2">
        {Object.entries(FLAG_META).map(([k, m]) => (
          <div
            key={k}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
            style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}` }}
          >
            <span className="font-semibold">{m.label}</span>
            <span>· {data?.counts?.[k] ?? 0}</span>
          </div>
        ))}
      </div>

      {dates.map(d => (
        <Card key={d} className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <p className="text-white font-semibold text-sm mb-3">
              {format(new Date(d + 'T00:00:00'), 'EEEE d MMM')}
            </p>
            <div className="space-y-2">
              {byDate[d].map(r => {
                const m = FLAG_META[r.flag] || FLAG_META.logged_not_scheduled;
                return (
                  <div
                    key={`${r.person_id}-${r.date}`}
                    className="p-3 bg-[#1E2530] rounded-xl"
                    data-testid={`reconcile-row-${r.person_id}-${r.date}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <User className="w-4 h-4 text-purple-400 shrink-0" />
                      <span className="text-white font-medium text-sm">{r.person_name}</span>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}` }}
                      >
                        {m.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs flex-wrap" style={{ color: '#9CA3AF' }}>
                      <span>Scheduled: <span className="text-gray-300">{refText(r.scheduled)}</span></span>
                      <ArrowRight className="w-3 h-3" />
                      <span>Logged: <span className="text-gray-300">{refText(r.logged)}</span></span>
                    </div>
                    {(onOpenSchedule || onLogInDiary) && (
                      <div className="flex gap-2 mt-2.5 flex-wrap">
                        {r.flag === 'scheduled_not_logged' && onLogInDiary && (
                          <button
                            type="button"
                            onClick={() => onLogInDiary(r)}
                            className="text-[12px] font-semibold inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors"
                            style={{ background: '#60A5FA', color: '#0B1220' }}
                            data-testid={`reconcile-log-diary-${r.person_id}-${r.date}`}
                          >
                            <Plus className="w-3.5 h-3.5" /> Log in diary
                          </button>
                        )}
                        {onOpenSchedule && (
                          <button
                            type="button"
                            onClick={() => onOpenSchedule(r)}
                            className="text-[12px] font-semibold inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors"
                            style={{ background: 'rgba(245,166,35,0.15)', color: '#F5A623', border: '1px solid rgba(245,166,35,0.3)' }}
                            data-testid={`reconcile-open-schedule-${r.person_id}-${r.date}`}
                          >
                            <CalendarPlus className="w-3.5 h-3.5" /> Open schedule
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function PortalTeamSchedule() {
  const { api, user } = useAuth();
  // OA-1 §Tier 2 — surveyors do NOT see "Keep Both" (defence-in-depth also
  // enforced at the API layer).
  const isAdmin = user?.user_type === 'admin' || user?.roles?.includes('admin');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('schedule'); // 'schedule' | 'reconcile'
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState([]);
  const [weekStart, setWeekStart] = useState(() => {
    // Deep link from the Site Diary: ?project=<id>&date=YYYY-MM-DD
    const d = new URLSearchParams(window.location.search).get('date');
    const parsed = d ? new Date(`${d}T00:00:00`) : null;
    return startOfWeek(parsed && !isNaN(parsed) ? parsed : new Date(), { weekStartsOn: 1 });
  });
  // Optional single-job lens (chip with a clear button); flags stay team-wide.
  const [projectFilter, setProjectFilter] = useState(() => new URLSearchParams(window.location.search).get('project') || null);
  
  // Schedule options
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [teamFallback, setTeamFallback] = useState(false);

  // Duplicate detection state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [duplicateStep, setDuplicateStep] = useState('choice'); // 'choice' | 'note' | 'confirm_move'
  const [duplicateNote, setDuplicateNote] = useState('');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    project_id: '',
    date: '',
    employee_ids: []
  });

  useEffect(() => {
    fetchSchedule();
    fetchScheduleOptions();
  }, [weekStart]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const startDate = format(weekStart, 'yyyy-MM-dd');
      const endDate = format(addDays(weekStart, 6), 'yyyy-MM-dd');
      const response = await api.get(`/portal/team-schedule?start_date=${startDate}&end_date=${endDate}`);
      setSchedule(response.data);
    } catch (error) {
      console.error('Failed to fetch team schedule:', error);
      toast.error('Failed to load team schedule');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduleOptions = async () => {
    try {
      const response = await api.get('/portal/schedule-options');
      setEmployees(response.data.employees || []);
      setProjects(response.data.projects || []);
      setTeam(response.data.team || []);
      setTeamFallback(!!response.data.team_fallback);
    } catch (error) {
      console.error('Failed to fetch schedule options:', error);
    }
  };

  // Weekly flags (shared with the desktop Schedule): double-booked = same person
  // on 2+ different jobs the same day (any creator); unscheduled = a team member
  // with no job at all this week — clears as soon as anyone gives them one.
  const { dupKeys, doubleBooked, scheduledIds, unscheduled } = useMemo(
    () => computeScheduleFlags(schedule.map(r => ({ schedule_id: r.id, date: r.date, project_id: r.project_id, project_ref: r.project_ref, employee_id: r.employee_id, employee_name: r.user_name })), team),
    [schedule, team],
  );
  const jobsOn = (empId, date) => [...new Set(schedule.filter(r => r.employee_id === empId && r.date === date && r.id !== selectedSchedule?.id).map(r => r.project_ref))];

  const navigateWeek = (direction) => {
    setWeekStart(prev => addWeeks(prev, direction));
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Group schedule by date - dedupe by schedule ID
  const getScheduleForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const daySchedules = schedule.filter(entry => entry.date === dayStr && (!projectFilter || entry.project_id === projectFilter));
    
    // Dedupe by schedule ID - combine people
    const scheduleMap = {};
    daySchedules.forEach(entry => {
      if (!scheduleMap[entry.id]) {
        scheduleMap[entry.id] = {
          ...entry,
          people: [{ id: entry.employee_id, name: entry.user_name }]
        };
      } else {
        scheduleMap[entry.id].people.push({ id: entry.employee_id, name: entry.user_name });
      }
    });
    
    return Object.values(scheduleMap);
  };

  const openAddDialog = (day, presetIds = []) => {
    setEditMode(false);
    setSelectedSchedule(null);
    setSelectedDate(day);
    setFormData({
      project_id: '',
      date: format(day, 'yyyy-MM-dd'),
      employee_ids: presetIds
    });
    setDialogOpen(true);
  };

  const openEditDialog = (entry, day) => {
    setEditMode(true);
    setSelectedSchedule(entry);
    setSelectedDate(day);
    setFormData({
      project_id: entry.project_id,
      date: entry.date,
      employee_ids: entry.employee_ids || []
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (resolution = null, note = null) => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (formData.employee_ids.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    // For new assignments (not edits), check for duplicates first
    if (!editMode && !resolution) {
      try {
        for (const empId of formData.employee_ids) {
          const res = await api.get(`/schedules/check-duplicate?employee_id=${empId}&date=${formData.date}`);
          if (res.data.has_duplicate) {
            const empName = employees.find(e => e.id === empId)?.name || 'Employee';
            setDuplicateData({ empName, empId, assignments: res.data.existing_assignments });
            setDuplicateStep('choice');
            setDuplicateNote('');
            setDuplicateDialogOpen(true);
            return; // Stop — let user resolve via dialog
          }
        }
      } catch (dupCheckErr) {
        // Duplicate-check is a UX affordance; if the endpoint fails we let
        // the create call through — the server-side unique constraint will
        // still guard against real duplicates. Surface the failure to the
        // console so ops can spot recurring issues.
        console.warn('Duplicate-shift check failed, proceeding anyway:', dupCheckErr);
      }
    }

    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (resolution) payload.duplicate_resolution = resolution;
      if (note) payload.duplicate_note = note;

      if (editMode && selectedSchedule) {
        await api.put(`/portal/team-schedule/${selectedSchedule.id}`, payload);
        toast.success('Schedule updated successfully');
      } else {
        await api.post('/portal/team-schedule', payload);
        toast.success('Schedule created successfully');
      }
      setDialogOpen(false);
      setDuplicateDialogOpen(false);
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await api.delete(`/portal/team-schedule/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(empId)
        ? prev.employee_ids.filter(id => id !== empId)
        : [...prev.employee_ids, empId]
    }));
  };

  return (
    <div className="p-4 space-y-5">
      {/* Header */}
      <BackToDiary />
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Team Schedule</h1>
      </div>

      {projectFilter && (
        <div className="flex items-center gap-2 rounded-lg px-3 py-2 bg-[#F5A623]/10 border border-[#F5A623]/30" data-testid="team-schedule-project-chip">
          <span className="text-xs text-gray-300">Showing</span>
          <span className="text-xs font-semibold text-[#F5A623] truncate">
            {(schedule.find(r => r.project_id === projectFilter) || projects.find(p => p.id === projectFilter) || {}).project_ref || 'this job'} only
          </span>
          <button
            type="button"
            onClick={() => setProjectFilter(null)}
            className="ml-auto text-xs text-gray-300 hover:text-white underline"
            data-testid="team-schedule-show-all-btn"
          >
            Show all jobs
          </button>
        </div>
      )}

      {/* Week Navigation */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateWeek(-1)}
              className="text-gray-400 hover:text-white min-h-[48px] min-w-[48px] active:scale-95 transition-transform"
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <div className="text-center">
              <p className="text-white font-semibold text-base">
                {format(weekStart, 'd MMM')} - {format(addDays(weekStart, 6), 'd MMM yyyy')}
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateWeek(1)}
              className="text-gray-400 hover:text-white min-h-[48px] min-w-[48px] active:scale-95 transition-transform"
              data-testid="next-week-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* View toggle — Schedule week view vs Plan-vs-Actual reconciliation */}
      <div className="flex gap-2" data-testid="schedule-view-toggle">
        <Button
          onClick={() => setView('schedule')}
          className={`flex-1 h-11 ${view === 'schedule' ? 'bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]' : 'bg-[#2A3240] text-gray-300 hover:text-white border border-white/10'}`}
          data-testid="schedule-view-tab"
        >
          Schedule
        </Button>
        <Button
          onClick={() => setView('reconcile')}
          className={`flex-1 h-11 ${view === 'reconcile' ? 'bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]' : 'bg-[#2A3240] text-gray-300 hover:text-white border border-white/10'}`}
          data-testid="reconcile-view-tab"
        >
          Plan vs Actual
        </Button>
      </div>

      {/* Content — Schedule week view or Plan-vs-Actual reconciliation */}
      {view === 'reconcile' ? (
        <ReconcilePanel
          api={api}
          weekStart={weekStart}
          onOpenSchedule={(r) => {
            setWeekStart(startOfWeek(new Date(r.date + 'T00:00:00'), { weekStartsOn: 1 }));
            setView('schedule');
          }}
          onLogInDiary={(r) => {
            const pid = (r.scheduled[0] || r.logged[0] || {}).project_id;
            if (!pid) return;
            navigate(`/portal/diary/${pid}`, { state: { seedDate: r.date, labourSeed: { people: [{ source: r.user_type === 'sub_contractor' ? 'subcontractor' : 'employee', id: r.person_id, name: r.person_name }] } } });
          }}
        />
      ) : loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
        </div>
      ) : (
        /* Week View */
        <div className="space-y-4">
          {(unscheduled.length > 0 || doubleBooked.length > 0) && (
            <Card className="bg-[#2A3240] border-amber-500/30" data-testid="schedule-flags-card">
              <CardContent className="p-4 space-y-4">
                {unscheduled.length > 0 && (
                  <div data-testid="unscheduled-panel">
                    <div className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-amber-400" />
                      <p className="text-white font-semibold text-sm">Not scheduled this week</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="unscheduled-count">{unscheduled.length}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Your contractors with no job this week. Tap a name to give them one.</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {unscheduled.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => openAddDialog(weekDays.find(d => isToday(d)) || weekDays[0], [p.id])}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
                          data-testid={`unscheduled-chip-${p.id}`}
                        >
                          <Plus className="w-3 h-3 inline mr-1 -mt-0.5" />{p.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {doubleBooked.length > 0 && (
                  <div data-testid="double-booked-panel">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <p className="text-white font-semibold text-sm">On two jobs the same day</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30" data-testid="double-booked-count">{doubleBooked.length}</span>
                    </div>
                    <div className="space-y-1 mt-2">
                      {doubleBooked.map(d => (
                        <p key={`${d.date}-${d.id}`} className="text-xs text-gray-300" data-testid={`double-booked-row-${d.id}-${d.date}`}>
                          <span className="text-white font-medium">{d.name}</span> · {format(new Date(d.date + 'T00:00:00'), 'EEE d MMM')} · {d.refs.join(' + ')}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {weekDays.map(day => {
            const daySchedule = getScheduleForDay(day);
            const today = isToday(day);
            
            return (
              <Card 
                key={day.toISOString()} 
                className={`border-white/10 ${today ? 'bg-[#F5A623]/10 border-[#F5A623]/30 border-2' : 'bg-[#2A3240]'}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 ${today ? 'bg-[#F5A623]' : 'bg-[#1E2530]'}`}>
                      <span className={`text-xs font-semibold ${today ? 'text-[#1E2530]' : 'text-gray-400'}`}>
                        {format(day, 'EEE')}
                      </span>
                      <span className={`text-xl font-bold ${today ? 'text-[#1E2530]' : 'text-white'}`}>
                        {format(day, 'd')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      {daySchedule.length === 0 ? (
                        <div className="h-14 flex items-center justify-between">
                          <p className="text-gray-500">No assignments</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAddDialog(day)}
                            className="text-[#F5A623] hover:text-[#D98E1D] h-12 w-12"
                            data-testid={`add-schedule-${format(day, 'yyyy-MM-dd')}`}
                          >
                            <Plus className="w-6 h-6" />
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {daySchedule.map((entry, index) => (
                            <div key={`${entry.id}-${index}`} className="p-3 bg-[#1E2530] rounded-xl">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start gap-2 mb-2">
                                    <Users className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                                    <span className="text-purple-400 font-medium text-sm flex flex-wrap gap-x-1.5 gap-y-1">
                                      {entry.people.map((p, i) => (
                                        <span key={p.id} className="inline-flex items-center gap-1">
                                          {p.name}{i < entry.people.length - 1 ? ',' : ''}
                                          {dupKeys.has(`${entry.date}|${p.id}`) && (
                                            <span
                                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30"
                                              data-testid={`double-booked-${entry.id}-${p.id}`}
                                            >
                                              2 jobs
                                            </span>
                                          )}
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-white font-semibold">{entry.project_ref}</span>
                                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                                      {entry.status || 'Assigned'}
                                    </Badge>
                                  </div>
                                  {entry.address && (
                                    <div className="flex items-start gap-2 mt-2 text-gray-500 text-sm">
                                      <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                                      <span className="line-clamp-2">{entry.address}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditDialog(entry, day)}
                                    className="text-gray-400 hover:text-white h-10 w-10 p-0"
                                    data-testid={`edit-schedule-${entry.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(entry.id)}
                                    className="text-gray-400 hover:text-red-400 h-10 w-10 p-0"
                                    data-testid={`delete-schedule-${entry.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* Add more button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAddDialog(day)}
                            className="w-full text-[#F5A623] hover:text-[#D98E1D] hover:bg-[#F5A623]/10 h-12"
                            data-testid={`add-more-${format(day, 'yyyy-MM-dd')}`}
                          >
                            <Plus className="w-5 h-5 mr-2" />
                            Add Assignment
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Schedule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CalendarPlus className="w-5 h-5 text-[#F5A623]" />
              {editMode ? 'Edit Schedule' : 'Add Schedule'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Date */}
            <div className="space-y-2">
              <Label className="text-gray-300">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="h-12 bg-[#1E2530] border-white/20 text-white"
              />
            </div>

            {/* Project Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300">Project *</Label>
              <Select 
                value={formData.project_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, project_id: value }))}
              >
                <SelectTrigger className="h-12 bg-[#1E2530] border-white/20 text-white">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                  {projects.map(project => (
                    <SelectItem 
                      key={project.id} 
                      value={project.id}
                      className="text-white focus:bg-[#F5A623]/20 py-2"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{project.project_ref}</span>
                        <span className="text-xs text-gray-400">{project.client_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employee Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-gray-300">Assign Employees *</Label>
                {employees.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      employee_ids: prev.employee_ids.length === employees.length ? [] : employees.map(e => e.id),
                    }))}
                    className="text-xs font-semibold text-[#F5A623] hover:text-[#D98E1D]"
                    data-testid="schedule-select-all-btn"
                  >
                    {formData.employee_ids.length === employees.length ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2 bg-[#1E2530] p-3 rounded-lg">
                {employees.length === 0 ? (
                  <p className="text-gray-500 text-sm" data-testid="no-employees-msg">No employees or sub-contractors yet — add them in Settings → Staff.</p>
                ) : (
                  employees.map(emp => {
                    const elsewhere = jobsOn(emp.id, formData.date);
                    const noJob = team.some(t => t.id === emp.id) && !scheduledIds.has(emp.id);
                    return (
                    <div
                      key={emp.id}
                      className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg cursor-pointer"
                      onClick={() => toggleEmployee(emp.id)}
                      data-testid={`schedule-emp-${emp.id}`}
                    >
                      <Checkbox
                        checked={formData.employee_ids.includes(emp.id)}
                        onCheckedChange={() => toggleEmployee(emp.id)}
                        className="border-white/30"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium">{emp.name}</p>
                        <p className="text-xs text-gray-400">
                          {emp.trade || (emp.user_type === 'sub_contractor' ? 'Sub-contractor' : 'Employee')}
                        </p>
                      </div>
                      {elsewhere.length > 0 ? (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 shrink-0 max-w-[45%] truncate" title={`Already on ${elsewhere.join(', ')}`} data-testid={`emp-elsewhere-${emp.id}`}>
                          Already on {elsewhere.join(', ')}
                        </span>
                      ) : noJob ? (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0" data-testid={`emp-nojob-${emp.id}`}>
                          No job this week
                        </span>
                      ) : null}
                    </div>
                    );
                  })
                )}
              </div>
              {teamFallback && (
                <p className="text-xs text-amber-300/90" data-testid="team-fallback-hint">
                  No team set for you yet, so everyone is listed. An admin can pick your contractors in Settings → Staff.
                </p>
              )}
              {formData.employee_ids.length > 0 && (
                <p className="text-sm text-gray-400">
                  {formData.employee_ids.length} employee(s) selected
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 h-12 border-white/20 text-gray-300 hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit()}
                disabled={submitting}
                className="flex-1 h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
                data-testid="save-schedule-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : editMode ? (
                  'Update'
                ) : (
                  'Add Schedule'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Dialog */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {duplicateData?.empName} is already assigned
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {duplicateData?.assignments?.map((a, i) => (
                <div key={a.project_id || a.id || i} className="mt-2 p-2 rounded bg-[#1E2530] border border-white/10 text-sm">
                  <p className="text-white font-medium">{a.project_ref} — {a.project_name}</p>
                  {a.project_address && <p className="text-gray-400 text-xs mt-0.5">{a.project_address}</p>}
                  {a.other_employees?.length > 0 && (
                    <p className="text-gray-500 text-xs mt-1">Also on shift: {a.other_employees.map(e => e.name).join(', ')}</p>
                  )}
                </div>
              ))}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {duplicateStep === 'choice' && (
            <div className="space-y-2 mt-2">
              <Button
                onClick={() => setDuplicateStep('confirm_move')}
                className="w-full bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30"
                data-testid="dup-move-btn"
              >
                Move to new assignment
              </Button>
              {isAdmin && (
                <Button
                  onClick={() => setDuplicateStep('note')}
                  variant="outline"
                  className="w-full border-white/20 text-gray-300 hover:bg-white/5"
                  data-testid="dup-keep-both-btn"
                >
                  Keep both shifts
                </Button>
              )}
              <AlertDialogCancel className="w-full bg-transparent border-white/10 text-gray-400 hover:bg-white/5" data-testid="dup-cancel-btn">
                Cancel
              </AlertDialogCancel>
            </div>
          )}

          {duplicateStep === 'note' && (
            <div className="space-y-3 mt-2">
              <Label className="text-gray-300 text-sm">Reason for dual assignment (required)</Label>
              <Textarea
                value={duplicateNote}
                onChange={(e) => setDuplicateNote(e.target.value)}
                placeholder="Explain why this employee needs two shifts on the same day"
                className="bg-[#1E2530] border-white/20 text-white min-h-[80px]"
                data-testid="dup-note-textarea"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => { handleSubmit('kept_both', duplicateNote); }}
                  disabled={duplicateNote.trim().length < 10 || submitting}
                  className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] disabled:opacity-50"
                  data-testid="dup-confirm-keep-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Keep Both'}
                </Button>
                <Button onClick={() => setDuplicateStep('choice')} variant="outline" className="border-white/20 text-gray-400">
                  Back
                </Button>
              </div>
              {duplicateNote.trim().length > 0 && duplicateNote.trim().length < 10 && (
                <p className="text-amber-400 text-xs">Minimum 10 characters required ({duplicateNote.trim().length}/10)</p>
              )}
            </div>
          )}

          {duplicateStep === 'confirm_move' && (
            <div className="space-y-3 mt-2">
              <p className="text-amber-400 text-sm">
                This will remove {duplicateData?.empName} from: {duplicateData?.assignments?.map(a => a.project_ref).join(', ')}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => { handleSubmit('moved'); }}
                  disabled={submitting}
                  className="flex-1 bg-red-500 text-white hover:bg-red-600"
                  data-testid="dup-confirm-move-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Move'}
                </Button>
                <Button onClick={() => setDuplicateStep('choice')} variant="outline" className="border-white/20 text-gray-400">
                  Back
                </Button>
              </div>
            </div>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
